// Контракты ответов задаются моделями; запросы не хранят состояние фичи.
import {
  type ModelCardTemplateId,
  modelBanksResponse,
  modelCardBlockPrepareResponse,
  modelCardIssuePrepareResponse,
  modelCardIssueResponse,
  modelCardPinResponse,
  modelCardResponse,
  modelCardSensitiveResponse,
  modelCardSetPinResponse,
  modelCardsResponse,
  modelCardTemplatesResponse,
  modelCardTopupPrepareResponse,
  modelCardTopupResponse,
  modelCardWithdrawPrepareResponse,
  modelCardWithdrawResponse,
  modelEmailCreateResponse,
  modelEmailVerifyResponse,
  modelPhoneCreateResponse,
  modelPhoneRetryResponse,
  modelPhoneVerifyResponse,
  modelSbpStateResponse,
  modelTelegramConfirmLinkResponse,
  modelTelegramLinkingTokenResponse,
  modelTransactionResponse,
  modelTransactionsResponse,
  modelUserMeResponse,
} from '@shared-core/features/backend/models'
import { makeAPI } from '@shared-core/services/api/client'

type TransactionParams = { params?: { per?: number; status?: string } }
type IdempotencyBody = { data: { idempotency: string } }
type Route = { routeParams: { id: string } }

export const apiGetBanks = makeAPI<typeof modelBanksResponse>({
  method: 'get',
  parser: modelBanksResponse,
  url: '/api/v1/banks',
})
export const apiGetCards = makeAPI<typeof modelCardsResponse>({
  method: 'get',
  parser: modelCardsResponse,
  url: '/api/v1/cards',
})
export const apiGetCardTemplates = makeAPI<typeof modelCardTemplatesResponse>({
  method: 'get',
  parser: modelCardTemplatesResponse,
  url: '/api/v1/cards/templates',
})
export const apiGetCard = makeAPI<typeof modelCardResponse, Route>({
  method: 'get',
  parser: modelCardResponse,
  url: '/api/v1/cards/:id',
})
export const apiGetCardPin = makeAPI<typeof modelCardPinResponse, Route>({
  method: 'get',
  parser: modelCardPinResponse,
  url: '/api/v1/cards/:id/pin',
  silentErrorCodes: ['CARD_NOT_FOUND', 'INVALID_CARD_STATUS', 'PIN_UNAVAILABLE', 'PROVIDER_UNAVAILABLE'],
})

export const apiSetCardPin = makeAPI<
  typeof modelCardSetPinResponse,
  Route & { data: { password: string; checkPassword: string } }
>({
  method: 'post',
  parser: modelCardSetPinResponse,
  url: '/api/v1/cards/:id/pin',
  isLoggerEnabled: false,
  silentErrorCodes: [
    'INVALID_PIN_FORMAT',
    'PIN_MISMATCH',
    'WEAK_PIN',
    'PIN_REQUIRES_PROVIDER_PAGE',
    'SET_PIN_FAILED',
    'PROVIDER_UNAVAILABLE',
  ],
})

export const apiGetCardSensitive = makeAPI<typeof modelCardSensitiveResponse, Route>({
  method: 'post',
  parser: modelCardSensitiveResponse,
  url: '/api/v1/cards/:id/sensitive',
})
export const apiPrepareIssueCard = makeAPI<
  typeof modelCardIssuePrepareResponse,
  { data: { templateId: ModelCardTemplateId } }
>({
  method: 'post',
  parser: modelCardIssuePrepareResponse,
  url: '/api/v1/cards/issue/prepare',
})
export const apiIssueCard = makeAPI<
  typeof modelCardIssueResponse,
  { data: { templateId: ModelCardTemplateId; idempotency: string } }
>({
  method: 'post',
  parser: modelCardIssueResponse,
  url: '/api/v1/cards/issue',
})
export const apiPrepareCardBlock = makeAPI<typeof modelCardBlockPrepareResponse, Route>({
  method: 'post',
  parser: modelCardBlockPrepareResponse,
  url: '/api/v1/cards/:id/block_prepare',
})
export const apiBlockCard = makeAPI<typeof modelCardResponse, Route & { data: { idempotency: string; code: string } }>({
  method: 'post',
  parser: modelCardResponse,
  url: '/api/v1/cards/:id/block',
})
export const apiFreezeCard = makeAPI<typeof modelCardResponse, Route>({
  method: 'post',
  parser: modelCardResponse,
  url: '/api/v1/cards/:id/freeze',
})
export const apiUnfreezeCard = makeAPI<typeof modelCardResponse, Route>({
  method: 'post',
  parser: modelCardResponse,
  url: '/api/v1/cards/:id/unfreeze',
})
export const apiPrepareCardTopup = makeAPI<typeof modelCardTopupPrepareResponse, Route & { data: { amount: string } }>({
  method: 'post',
  parser: modelCardTopupPrepareResponse,
  url: '/api/v1/cards/:id/topup/prepare',
})
export const apiTopupCard = makeAPI<typeof modelCardTopupResponse, Route & IdempotencyBody>({
  method: 'post',
  parser: modelCardTopupResponse,
  url: '/api/v1/cards/:id/topup',
})
export const apiPrepareCardWithdraw = makeAPI<
  typeof modelCardWithdrawPrepareResponse,
  Route & { data: { amount: string; bankId: string; phone: string } }
>({
  method: 'post',
  parser: modelCardWithdrawPrepareResponse,
  url: '/api/v1/cards/:id/withdraw/prepare',
})
export const apiWithdrawCard = makeAPI<typeof modelCardWithdrawResponse, Route & IdempotencyBody>({
  method: 'post',
  parser: modelCardWithdrawResponse,
  url: '/api/v1/cards/:id/withdraw',
})
export const apiGetTransactions = makeAPI<typeof modelTransactionsResponse, TransactionParams>({
  method: 'get',
  parser: modelTransactionsResponse,
  url: '/api/v1/transactions',
})
export const apiGetTransaction = makeAPI<typeof modelTransactionResponse, Route>({
  method: 'get',
  parser: modelTransactionResponse,
  url: '/api/v1/transactions/:id',
})
export const apiGetSbpPayment = makeAPI<typeof modelSbpStateResponse, Route>({
  method: 'get',
  parser: modelSbpStateResponse,
  url: '/api/v1/pay/sbp/check/:id',
})
export const apiGetCurrentUser = makeAPI({ method: 'get', parser: modelUserMeResponse, url: '/api/v1/user/me' })
export const apiCreateUserPhone = makeAPI<typeof modelPhoneCreateResponse, { data: { phone: string } }>({
  method: 'post',
  parser: modelPhoneCreateResponse,
  url: '/api/v1/user/phone/create',
})
export const apiRetryUserPhone = makeAPI<typeof modelPhoneRetryResponse, IdempotencyBody>({
  method: 'post',
  parser: modelPhoneRetryResponse,
  url: '/api/v1/user/phone/retry',
})
export const apiVerifyUserPhone = makeAPI<
  typeof modelPhoneVerifyResponse,
  { data: { idempotency: string; otp: string } }
>({
  method: 'post',
  parser: modelPhoneVerifyResponse,
  url: '/api/v1/user/phone/verify',
})
export const apiConfirmTelegramLink = makeAPI<
  typeof modelTelegramConfirmLinkResponse,
  { data: { linking_token: string } }
>({
  method: 'post',
  parser: modelTelegramConfirmLinkResponse,
  url: '/api/v1/auth/telegram/confirm_link',
})
export const apiRequestTelegramLinkingToken = makeAPI<typeof modelTelegramLinkingTokenResponse>({
  method: 'post',
  parser: modelTelegramLinkingTokenResponse,
  url: '/api/v1/auth/telegram/linking_token',
})
export { apiLoginTma } from '@shared-core/features/auth/api'

export const apiCreateUserEmail = makeAPI<typeof modelEmailCreateResponse, { data: { email: string } }>({
  method: 'post',
  parser: modelEmailCreateResponse,
  url: '/api/v1/user/email/create',
})
export const apiVerifyUserEmail = makeAPI<
  typeof modelEmailVerifyResponse,
  { data: { code: string; idempotency: string } }
>({
  method: 'post',
  parser: modelEmailVerifyResponse,
  url: '/api/v1/user/email/verify',
  silentErrorCodes: ['INVALID_CODE'],
  silentErrorStatuses: [404, 406, 422],
})
